import { Container, Sprite, Text, Texture } from 'pixi.js';
import type { Game } from './Game';
import { PowerNames } from '../engine/JuniValues';
import { KnyttPoint } from '../engine/KnyttPoint';
import { loadObjectFrames } from './objects/ObjectGraphics';

// PowerItemObject.OBJ2POWER inverted: power index (0..12, matching PowerNames) -> its
// bank-0 PowerItem object id (used purely to fetch the same icon art the pickup uses).
const POWER_ICON_OBJECT_Y = [3, 4, 5, 6, 7, 8, 9, 10, 21, 22, 23, 24, 35];
const OWNED_ALPHA = 1;
const UNOWNED_ALPHA = 0.18;

/**
 * Q-key info overlay (InfoPanel.cs/ArtifactsPanel.cs): a row of power icons
 * (dimmed until owned) plus coin/artifact/creature counts, shown while Q is
 * held. Rendered as Pixi content (not a DOM overlay) so it can reuse the same
 * in-world sprite art — including any custom per-world tileset — the pickups
 * themselves use.
 */
export class InfoPanel {
  readonly container = new Container();
  private icons: Sprite[] = [];
  private coinText: Text;
  private artifactText: Text;
  private creatureText: Text;
  private hasCoins = false;
  private hasArtifacts = false;
  private hasCreatures = false;

  constructor(private game: Game) {
    this.container.visible = false;
    this.container.x = 4;
    this.container.y = 4;

    const textStyle = { fontFamily: 'monospace', fontSize: 12, fill: 0xffffff };
    this.coinText = new Text({ text: '', style: textStyle });
    this.artifactText = new Text({ text: '', style: textStyle });
    this.creatureText = new Text({ text: '', style: textStyle });
    this.coinText.visible = false;
    this.artifactText.visible = false;
    this.creatureText.visible = false;
  }

  async load(): Promise<void> {
    let x = 0;
    for (const y of POWER_ICON_OBJECT_Y) {
      const gfx = await loadObjectFrames(this.game, new KnyttPoint(0, y));
      const sprite = new Sprite(gfx?.frames[0] ?? Texture.WHITE);
      sprite.x = x;
      sprite.alpha = UNOWNED_ALPHA;
      this.container.addChild(sprite);
      this.icons.push(sprite);
      x += 15;
    }
    this.coinText.x = x + 4;
    this.artifactText.x = x + 4;
    this.creatureText.x = x + 4;
    this.coinText.y = 0;
    this.artifactText.y = 12;
    this.creatureText.y = 24;
    this.container.addChild(this.coinText, this.artifactText, this.creatureText);
  }

  // UICanvasLayer.cs:73-82: Q is a toggle with a 0.25s StayTimer — a quick tap
  // leaves the panel open (release within the timer is ignored); holding longer
  // closes it again on release; a second press always toggles. The C# slide
  // animation is not ported (the panel just shows/hides).
  private showing = false;
  private stayTimer = 0;

  update(dt: number): void {
    const input = this.game.input;
    if (this.stayTimer > 0) this.stayTimer -= dt;
    if (input.justPressed('show_info')) {
      this.showing = !this.showing;
      this.stayTimer = 0.25;
    } else if (input.justReleased('show_info')) {
      // Mirrors togglePanel-on-release: only closes if it is open and the tap
      // outlived the stay timer.
      if (this.showing && this.stayTimer <= 0) this.showing = false;
    }
    const show = this.showing;
    this.container.visible = show;
    if (!show) return;

    // UICanvasLayer.cs:50-53: the Map icon only appears with map access.
    this.icons[PowerNames.Map].visible = this.game.hasMapAccess();

    const v = this.game.values;
    for (let i = 0; i < this.icons.length; i++) {
      this.icons[i].alpha = v.getPower(i as PowerNames) ? OWNED_ALPHA : UNOWNED_ALPHA;
    }

    if (!this.hasCoins && v.getCoinCount() > 0) {
      this.hasCoins = true;
      this.coinText.visible = true;
    }
    if (!this.hasArtifacts && v.getArtifactsCount() > 0) {
      this.hasArtifacts = true;
      this.artifactText.visible = true;
    }
    if (!this.hasCreatures && v.getCreaturesCount() > 0) {
      this.hasCreatures = true;
      this.creatureText.visible = true;
    }
    if (this.hasCoins) this.coinText.text = `Coins: ${v.getCoinCount()}`;
    if (this.hasArtifacts) this.artifactText.text = `Artifacts: ${v.getArtifactsCount()}`;
    if (this.hasCreatures) this.creatureText.text = `Creatures: ${v.getCreaturesCount()}`;
  }

  destroy(): void {
    this.container.destroy({ children: true });
  }
}
